import { m } from "#/paraglide/messages";
import {
  cn,
  createDataTableColumnHelper,
  DataTable,
  TableCellStack,
  Link as UiLink,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet, stripSearchParams, useParams } from "@tanstack/react-router";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { ShortDate } from "#/components/short-date";
import { StudentResultsSidebarFilters } from "#/modules/student/components/student-results-sidebar-filters";
import {
  getDefaultDateRange,
  SOCIAALRESULTATEN_SEARCH_DEFAULTS,
  sociaalresultatenSearchSchema,
} from "#/modules/student/schema.search.sociaalresultaten";
import { getStudentSociaalResults } from "#/modules/school.students/students.functions";
import {
  getLeerjaarComboTranslation,
  getSociaalEmotioneelDomeinTranslation,
} from "#/modules/util.i18n/enum-mapping";
import {
  OverachievedIconButton,
  ScoreStatusIndicatorLabel,
} from "#/modules/util.score-status/components/score-status";
import { getScoreBasedRowColor, getScoreStatus } from "#/modules/util.score-status/score-status";
import type { SociaalresultatenSearch } from "#/modules/student/schema.search.sociaalresultaten";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/social-results",
)({
  validateSearch: sociaalresultatenSearchSchema,
  // Keeps default filter values out of the URL.
  search: { middlewares: [stripSearchParams(SOCIAALRESULTATEN_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the server fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    from: search.from,
    to: search.to,
    includeWithProgress: search.includeWithProgress,
    includeWithoutProgress: search.includeWithoutProgress,
    selectedDomeinFilterIds: search.selectedDomeinFilterIds,
  }),
  loader: ({ deps, params }) =>
    getStudentSociaalResults({ data: { leerlingId: params.id, ...deps } }),
  component: SociaalEmotioneleResultaten,
});

type ProgressItem = Awaited<
  ReturnType<typeof getStudentSociaalResults>
>["sociaalEmotioneelThemaProgressItems"][number];

const helper = createDataTableColumnHelper<ProgressItem>();

const columns = helper.columns([
  helper.accessor("name", {
    header: "Thema",
    meta: { rowTarget: true, wrap: true },
    cell: ({ row, getValue }) => (
      <TableCellStack
        secondary={`${getSociaalEmotioneelDomeinTranslation(row.original.domein)} · ${row.original.reeksName}`}
      >
        <UiLink
          variant="secondary"
          underline
          to="/$locale/{-$schoolyear}/$school/students/$id/social-results/drawer/historie/$sociaalEmotioneelThemaId"
          params={(prev: Record<string, string>) => ({
            ...prev,
            sociaalEmotioneelThemaId: row.original.sociaalEmotioneelThemaId,
          })}
          search={(prev: SociaalresultatenSearch) => ({
            ...prev,
            sociaalEmotioneelThemasReeksId: row.original.sociaalEmotioneelThemasReeksId,
            leerjaarCombo: row.original.sociaalEmotioneelThemaLeerjaarCombo,
          })}
        >
          {getValue()}
        </UiLink>
      </TableCellStack>
    ),
  }),
  helper.display({
    id: "leerjaar",
    header: "Leerjaar",
    meta: { cellClassName: "text-muted-foreground" },
    cell: ({ row }) =>
      getLeerjaarComboTranslation(row.original.sociaalEmotioneelThemaLeerjaarCombo),
  }),
  helper.display({
    id: "resultaat",
    header: "Resultaat",
    cell: ({ row }) =>
      row.original.behaaldNiveau === 0 ? (
        "-"
      ) : (
        <span className="inline-flex items-center gap-1.5">
          <ScoreStatusIndicatorLabel
            status={getScoreStatus(row.original.behaaldNiveau, row.original.maxNiveau)}
            label={row.original.resultaat}
          />
          {row.original.isOverachieved && <OverachievedIconButton />}
        </span>
      ),
  }),
  helper.display({
    id: "laatstBehandeld",
    header: "Laatst behandeld",
    cell: ({ row }) =>
      row.original.behaaldAt ? <ShortDate value={row.original.behaaldAt} /> : "-",
  }),
]);

function SociaalEmotioneleResultaten() {
  const { sociaalEmotioneelThemaProgressItems, domeinFilter } = Route.useLoaderData();
  // Single source for both the sidebar filter controls and the table rows.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const childParams = useParams({ strict: false });
  const selectedSociaalEmotioneelThemaId = childParams.sociaalEmotioneelThemaId;
  const defaultDateRange = getDefaultDateRange();

  return (
    <Page.SidebarLayout>
      <Page.Sidebar>
        <StudentResultsSidebarFilters
          filterTree={domeinFilter}
          filterType="domein"
          defaultDateRange={defaultDateRange}
          filters={search}
          selectedFilterIds={search.selectedDomeinFilterIds}
          onFiltersChange={(partial) =>
            navigate({
              search: (prev) => ({ ...prev, ...partial }),
              replace: true,
              resetScroll: false,
            })
          }
          onFilterIdsChange={(ids) =>
            navigate({
              search: (prev) => ({ ...prev, selectedDomeinFilterIds: ids }),
              replace: true,
              resetScroll: false,
            })
          }
        />
      </Page.Sidebar>
      <PendingContent className="grow">
        <DataTable
          card
          aria-label={m["student.sociaal_resultaten_aria"]()}
          columns={columns}
          data={sociaalEmotioneelThemaProgressItems}
          getRowId={(item) => item.sociaalEmotioneelThemaId}
          empty={<p className="w-full text-center">Geen resultaten gevonden</p>}
          getRowClassName={(item) =>
            cn(
              getScoreBasedRowColor(item.behaaldNiveau),
              selectedSociaalEmotioneelThemaId === item.sociaalEmotioneelThemaId &&
                `relative z-10 shadow-[0_0_10px_2px_rgba(0,0,0,0.1),0_-1px_0px_rgba(0,0,0,0.05)_inset]`,
            )
          }
        />
      </PendingContent>
      <Outlet />
    </Page.SidebarLayout>
  );
}
